import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Alert, message, Space } from "antd";
import { LoginOutlined, LogoutOutlined, HomeOutlined, ClockCircleOutlined, CoffeeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { getCurrentPosition } from "../../helper/geolocation";
import { requestPushSubscription, registerPushSubscription } from "../../helper/pushNotifications";
import { AuthContext } from "../../context/AuthContext";
import PageTransition from "../../components/ui/PageTransition";
import StatusTag from "../../components/ui/StatusTag";

const LOCATION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes, mirrors backend default

const statusLabel = {
  wfh: "Work From Home",
  on_leave: "On Approved Leave",
  holiday: "Company Holiday",
};

const getGreeting = () => {
  const hour = moment().hour();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const Home = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const { userProfile } = React.useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [dayStatus, setDayStatus] = useState(null); // "wfh" | "leave" | "holiday" | null
  const [busy, setBusy] = useState(false);
  const [breakBusy, setBreakBusy] = useState(false);
  const [breakElapsedSec, setBreakElapsedSec] = useState(0);
  const intervalRef = useRef(null);
  const breakTimerRef = useRef(null);

  const fetchToday = () => {
    request({
      url: apiPath.attendanceToday,
      method: "GET",
      onSuccess: (res) => {
        setSession(res?.data?.session || null);
        setDayStatus(res?.data?.dayStatus || null);
      },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchToday, []);

  // Periodic location-check while a session is active (skipped for WFH/leave/holiday days).
  useEffect(() => {
    if (session?.isActive && !dayStatus) {
      intervalRef.current = setInterval(runLocationCheck, LOCATION_CHECK_INTERVAL_MS);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.isActive, session?._id, dayStatus]);

  // Live "how long have I been on break" ticker — purely a display convenience,
  // the actual max-break enforcement happens server-side regardless of whether
  // this tab is open.
  useEffect(() => {
    if (session?.onBreak && session?.breakStartedAt) {
      const tick = () => setBreakElapsedSec(Math.max(0, Math.floor((Date.now() - new Date(session.breakStartedAt).getTime()) / 1000)));
      tick();
      breakTimerRef.current = setInterval(tick, 1000);
    } else {
      setBreakElapsedSec(0);
    }
    return () => clearInterval(breakTimerRef.current);
  }, [session?.onBreak, session?.breakStartedAt]);

  const formatDuration = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  const runLocationCheck = async () => {
    if (!session?._id || !session?.isActive) return;
    try {
      const { lat, lng, accuracy } = await getCurrentPosition();
      request({
        url: apiPath.locationCheck,
        method: "POST",
        data: { sessionId: session._id, lat, lng, accuracy },
        // Runs silently every 5 minutes in the background — must not flash
        // the app-wide loading overlay over whatever the employee is doing.
        silent: true,
        onSuccess: (res) => {
          if (res?.data?.autoLoggedOut) {
            // Deliberately generic — doesn't reveal the geofence/periodic
            // location-check mechanism to the employee.
            message.warning("Your attendance session was closed automatically.");
            fetchToday();
          }
        },
      });
    } catch (err) {
      message.error("Could not read your location for the periodic check.");
    }
  };

  const handleLogin = async () => {
    setBusy(true);
    // Fired in the same click as the location request below (not chained
    // after it), same reasoning as Login.js's sign-in handler: this is the
    // moment an employee actually cares about push (auto-logout/geofence
    // alerts while their attendance session is active), but plenty of
    // people never see the sign-in prompt fire in time, or already had
    // Notification.permission decided before this device's login-time
    // prompt was added. Asking again here — still only when permission is
    // genuinely undecided — means an employee isn't left with no way to
    // get both prompts together in one sitting.
    const pushSubscriptionPromise =
      typeof Notification !== "undefined" && Notification.permission === "default"
        ? requestPushSubscription()
        : Promise.resolve(null);

    try {
      const { lat, lng, accuracy } = await getCurrentPosition();
      request({
        url: apiPath.attendanceLogin,
        method: "POST",
        data: { lat, lng, accuracy },
        onSuccess: (res) => {
          if (res?.status === 201) {
            message.success("Shift started");
            fetchToday();
          } else {
            message.error(res?.message || "Could not start attendance");
          }
          pushSubscriptionPromise.then((sub) => registerPushSubscription(request, sub));
        },
        onError: (err) => message.error(err?.response?.data?.message || "Could not start attendance"),
      });
    } catch (err) {
      message.error("Could not read your location. Please enable location access.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    setBusy(true);
    try {
      const { lat, lng, accuracy } = await getCurrentPosition();
      request({
        url: apiPath.attendanceLogout,
        method: "POST",
        data: { sessionId: session._id, lat, lng, accuracy },
        onSuccess: () => {
          message.success("Shift ended");
          fetchToday();
        },
      });
    } catch (err) {
      // still allow logout without geostamp if location unavailable
      request({
        url: apiPath.attendanceLogout,
        method: "POST",
        data: { sessionId: session._id, lat: null, lng: null },
        onSuccess: () => {
          message.success("Shift ended");
          fetchToday();
        },
      });
    } finally {
      setBusy(false);
    }
  };

  const handleStartBreak = () => {
    setBreakBusy(true);
    request({
      url: apiPath.breakStart,
      method: "POST",
      data: { sessionId: session._id },
      onSuccess: () => {
        message.success("Break started");
        fetchToday();
      },
      onError: (err) => message.error(err?.response?.data?.message || "Could not start break"),
    }).finally(() => setBreakBusy(false));
  };

  const handleEndBreak = () => {
    setBreakBusy(true);
    request({
      url: apiPath.breakEnd,
      method: "POST",
      data: { sessionId: session._id },
      onSuccess: () => {
        message.success("Break ended");
        fetchToday();
      },
      onError: (err) => message.error(err?.response?.data?.message || "Could not end break"),
    }).finally(() => setBreakBusy(false));
  };

  const active = session?.isActive;

  return (
    <PageTransition>
      <Card
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)",
          border: "none",
          overflow: "hidden",
        }}
        bodyStyle={{ padding: "24px 20px" }}
      >
        <div style={{ color: "#fff" }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {getGreeting()}, {userProfile?.name || "there"}
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{moment().format("dddd, MMMM D, YYYY")}</div>

          {userProfile?.workingHours?.start && userProfile?.workingHours?.end && (
            <div style={{ marginTop: 10, fontSize: 12.5, opacity: 0.85, display: "flex", alignItems: "center", gap: 6 }}>
              <ClockCircleOutlined />
              Working hours: {userProfile.workingHours.start} – {userProfile.workingHours.end}
            </div>
          )}
        </div>
      </Card>

      {dayStatus && (
        <Alert
          style={{ marginBottom: 16, borderRadius: "var(--i-ems-radius-sm)" }}
          type="info"
          showIcon
          icon={<HomeOutlined />}
          message={`Today is a ${statusLabel[dayStatus] || dayStatus} day — attendance check-in is not required.`}
        />
      )}

      <Card
        title="Today's Attendance"
        extra={
          <Space>
            <Button size="small" onClick={() => navigate("/wfh")}>
              Request WFH
            </Button>
            <Button size="small" onClick={() => navigate("/overtime", { state: { autoOpen: true } })}>
              Request OT
            </Button>
          </Space>
        }
      >
        {session ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <StatusTag status={session.status} label={statusLabel[session.status]} />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12,
              }}
            >
              {[
                { label: "Shift Start", value: session.loginAt ? moment(session.loginAt).format("HH:mm:ss") : "-" },
                { label: "Shift End", value: session.logoutAt ? moment(session.logoutAt).format("HH:mm:ss") : "-" },
              ].map((row) => (
                <div
                  key={row.label}
                  style={{
                    background: "var(--i-ems-bg)",
                    borderRadius: "var(--i-ems-radius-sm)",
                    padding: "10px 12px",
                    border: "1px solid var(--i-ems-border)",
                  }}
                >
                  <div style={{ fontSize: 11, color: "var(--i-ems-text-secondary)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {row.label}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{row.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : dayStatus ? (
          <p style={{ color: "var(--i-ems-text-secondary)" }}>
            No attendance session needed today — you're marked as {statusLabel[dayStatus] || dayStatus}.
          </p>
        ) : (
          <p style={{ color: "var(--i-ems-text-secondary)" }}>You haven't logged attendance today.</p>
        )}

        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10 }}>
          {!active ? (
            <Button type="primary" icon={<LoginOutlined />} size="large" block loading={busy} onClick={handleLogin}>
              Start Shift
            </Button>
          ) : (
            <>
              <Button danger icon={<LogoutOutlined />} size="large" block loading={busy} onClick={handleLogout}>
                End Shift
              </Button>
              {!session.onBreak ? (
                <Button icon={<CoffeeOutlined />} size="large" block loading={breakBusy} onClick={handleStartBreak}>
                  Start Break
                </Button>
              ) : (
                <Button type="dashed" icon={<CoffeeOutlined />} size="large" block loading={breakBusy} onClick={handleEndBreak}>
                  End Break — {formatDuration(breakElapsedSec)}
                </Button>
              )}
            </>
          )}
        </div>
      </Card>
    </PageTransition>
  );
};

export default Home;
